import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { login as apiLogin, refreshToken as apiRefreshToken, resolveApiBaseUrl } from '../services/api';
import sessionSync from '../services/sessionSync';

const useAuth = () => {
  const navigate = useNavigate();

  const getSsoCandidate = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const ssoToken = urlParams.get('token');
    const ssoUser = urlParams.get('user');
    if (!ssoToken || !ssoUser) return null;
    try {
      const userData = JSON.parse(decodeURIComponent(ssoUser));
      return { ...userData, token: ssoToken, __source: 'sso' };
    } catch (error) {
      return null;
    }
  };

  const stripAuthQueryParams = () => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('token');
      url.searchParams.delete('user');
      url.searchParams.delete('sso');
      url.searchParams.delete('redirect');
      url.searchParams.delete('timestamp');
      window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
    } catch (_) {}
  };

  const verifyTokenWithBackend = async (token) => {
    const base = resolveApiBaseUrl();
    const url = `${base}/api/auth/me`;
    const ctrl = new AbortController();
    const timeoutMs = 15000;
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    const resp = await fetch(url, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
      cache: 'no-store',
      signal: ctrl.signal
    }).catch((error) => {
      if (error?.name === 'AbortError') {
        const err = new Error('Validação de sessão excedeu o tempo limite');
        err.code = 'AUTH_ME_TIMEOUT';
        throw err;
      }
      throw error;
    }).finally(() => {
      clearTimeout(timer);
    });
    if (!resp.ok) {
      const err = new Error('Sessão inválida');
      err.status = resp.status;
      throw err;
    }
    const payload = await resp.json().catch(() => null);
    return payload;
  };

  // Inicializar user do localStorage (SSO é verificado antes de ativar sessão)
  const [user, setUser] = useState(() => {
    // Fallback para localStorage
    try {
      const stored = localStorage.getItem('vortiqaUser');
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      return null;
    }
  });
  
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(() => {
    try {
      return Boolean(localStorage.getItem('vortiqaUser')) || Boolean(getSsoCandidate());
    } catch (_) {
      return Boolean(getSsoCandidate());
    }
  });
  const isAuth = !!user;

  // Sincronizar estado de autenticação - OTIMIZADO
  useEffect(() => {
    if (user) {
      localStorage.setItem('vortiqaUser', JSON.stringify(user));
      if (user?.token) {
        localStorage.setItem('token', String(user.token));
      }
      sessionSync.sync(user);
    } else {
      localStorage.removeItem('vortiqaUser');
      localStorage.removeItem('token');
    }
  }, [user]);

  useEffect(() => {
    let cancelled = false;

    const restore = async () => {
      try {
        const candidate = getSsoCandidate();
        if (candidate?.token) {
          await verifyTokenWithBackend(candidate.token);
          if (!cancelled) {
            setUser(candidate);
            stripAuthQueryParams();
          }
          return;
        }

        if (!user?.token) return;
        await verifyTokenWithBackend(user.token);
      } catch (error) {
        if (!cancelled) {
          setUser(null);
          sessionSync.clearAllSessions();
          stripAuthQueryParams();
        }
      } finally {
        if (!cancelled) setRestoring(false);
      }
    };

    restore();
    return () => {
      cancelled = true;
    };
  }, []);

  // Listener para mudanças de sessão entre abas
  useEffect(() => {
    const handleSessionChange = (event) => {
      if (event.key === 'vortiqaUser') {
        if (event.newValue) {
          try {
            const userData = JSON.parse(event.newValue);
            setUser(userData);
          } catch (error) {
            setUser(null);
          }
        } else {
          setUser(null);
        }
      }
    };

    sessionSync.addListener(handleSessionChange);
    
    return () => {
      sessionSync.removeListener(handleSessionChange);
    };
  }, []);

  // Função de login
  const handleLogin = useCallback(async (email, password) => {
    if (typeof email === 'object') {
      // SSO Login - dados já fornecidos
      const userData = email;
      setUser(userData);
      return userData;
    }

    // Login normal
    setLoading(true);
    try {
      const userData = await apiLogin(email, password);
      
      // Atualizar estados de forma síncrona
      setUser(userData);
      
      return userData;
    } catch (error) {
      setUser(null);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Função de logout
  const handleLogout = useCallback(() => {
    setUser(null);
    sessionSync.clearAllSessions();
    navigate('/login');
  }, [navigate, user]);

  // Função para refresh do token
  const refreshUserToken = useCallback(async () => {
    if (!user?.refreshToken) {
      throw new Error('Refresh token não disponível');
    }

    try {
      const newToken = await apiRefreshToken(user.refreshToken);
      const updatedUser = { ...user, token: newToken };
      setUser(updatedUser);
      return newToken;
    } catch (error) {
      handleLogout();
      throw error;
    }
  }, [user, handleLogout]);

  // Função para verificar se o usuário tem uma permissão específica
  const hasPermission = useCallback((permission) => {
    if (!user || !user.permissions) return false;
    return user.permissions.includes(permission);
  }, [user]);

  // Função para verificar se o usuário tem um papel específico
  const hasRole = useCallback((role) => {
    if (!user || !user.role) return false;
    return user.role === role;
  }, [user]);

  return {
    user,
    isAuth,
    loading: loading || restoring,
    handleLogin,
    handleLogout,
    refreshUserToken,
    hasPermission,
    hasRole
  };
};

export default useAuth;
